import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { Modal, Button, Icon } from "../../../components/tailwind";
import {
  GenerationLogDetailLink,
  GenerationLogRows,
  GenerationLogRowTestIds,
  InfoRow,
} from "../../../components/GenerationLogRows";
import { getTextBlockGenerationLog, GenerationLog } from "../../../api/gptApi";
import { RootState } from "../../../utils/store";
import { SentenceGenerationInfo } from "../types";

type InfoTestIds = GenerationLogRowTestIds & {
  trigger: string;
  content: string;
  update: string;
  usedInstruction: string;
};

type Props = {
  promptName?: string;
  fallbackInstruction?: string;
  generationInfo?: SentenceGenerationInfo;
  /** Set for saved text blocks, letting the log be read back after a page load. */
  textBlockId?: string;
  testIds: InfoTestIds;
  showUpdateButton?: boolean;
  onUpdatePrompt: () => void;
};

/** The "i" affordance on a generated text block, opening the full generation log. */
export const GeneratedResultInfo: React.FC<Props> = ({
  promptName,
  fallbackInstruction,
  generationInfo,
  textBlockId,
  testIds,
  showUpdateButton,
  onUpdatePrompt,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const token = useSelector((state: RootState) => state.auth.token);
  // Cached per text block: this component instance is reused as sentences come and go
  // in a slot, so a log cached under the previous textBlockId must not be reused.
  const [cachedLog, setCachedLog] = useState<{
    textBlockId: string;
    log: GenerationLog;
  } | null>(null);
  const close = (): void => dialogRef.current?.close();

  const savedLog =
    cachedLog && cachedLog.textBlockId === textBlockId
      ? cachedLog.log
      : undefined;

  // A sentence generated in this session carries part of its log on the generation
  // response, but the model, images and web searches only exist on the saved event —
  // so always read the event back once, whether the sentence is fresh or loaded.
  const open = async (): Promise<void> => {
    dialogRef.current?.showModal();
    if (!textBlockId || savedLog) {
      return;
    }
    setCachedLog({
      textBlockId,
      log: await getTextBlockGenerationLog(token, textBlockId),
    });
  };

  return (
    <>
      <button
        type="button"
        className="tw-inline-flex tw-items-center tw-align-middle tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer tw-text-gray-500 hover:tw-text-gray-700"
        data-testid={testIds.trigger}
        onClick={open}
        title="Generation log"
        aria-label="Generation log"
      >
        <Icon name="info" className="tw-text-xl tw-leading-[1]" />
      </button>
      {/* Portalled to body: nested inside the Semantic card the dialog picks up an
          ancestor containing block and stops centering on the viewport. */}
      {ReactDOM.createPortal(
        <Modal dialogRef={dialogRef}>
          <Modal.Header divide>
            <h5 className="tw-text-lg tw-font-semibold">Generation log</h5>
          </Modal.Header>
          <Modal.Body className="tw-py-6">
            <div
              data-testid={testIds.content}
              className="tw-flex tw-flex-col tw-gap-4"
            >
              <GenerationLogDetailLink
                log={savedLog}
                testId={testIds.detailLink}
              />
              <InfoRow label="Prompt">{promptName ?? "Unknown"}</InfoRow>
              <InfoRow label="Instruction" testId={testIds.usedInstruction}>
                {generationInfo?.instruction ??
                  fallbackInstruction ??
                  "Unknown"}
              </InfoRow>
              <GenerationLogRows
                log={savedLog}
                fresh={generationInfo}
                testIds={testIds}
              />
            </div>
          </Modal.Body>
          <Modal.Actions>
            {showUpdateButton && (
              <Button
                variant="textual-blue-alt"
                type="button"
                data-testid={testIds.update}
                onClick={(): void => {
                  close();
                  onUpdatePrompt();
                }}
              >
                Update prompt
              </Button>
            )}
            <Button variant="secondary" type="button" onClick={close}>
              Close
            </Button>
          </Modal.Actions>
        </Modal>,
        document.body
      )}
    </>
  );
};
